import React, { useContext, useEffect, useRef, useState } from 'react';
import {
  arrayOf,
  bool,
  extend,
  params,
  QueryContext,
  shape,
  string,
  useDataModel
} from '@thd-nucleus/data-sources';
import { node } from 'prop-types';
import { Button, Col, Row } from '@thd-olt-component-react/core-ui';
import { ExperienceContext, useStoreId } from '@thd-nucleus/experience-context';
import { PIPRedirector } from '@thd-nucleus/app-render';
import { AisleBay } from '@thd-olt-component-react/aisle-bay';
import { AugmentedReality } from '@thd-olt-component-react/augmented-reality';
import { Breadcrumbs } from '@thd-olt-component-react/breadcrumbs';
import { Buybox } from '@thd-olt-component-react/buybox';
import { ConsumerCardMessaging } from '@thd-olt-component-react/consumer-card-messaging';
import { Discontinued } from '@thd-olt-component-react/discontinued';
import { EducationalSummary } from '@thd-olt-component-react/educational-summary';
import { FrequentlyBoughtTogether } from '@thd-olt-component-react/frequently-bought-together';
import { GlobalCustomConfigurator } from '@thd-olt-component-react/global-custom-configurator';
import { HdhomeCatalogPromo } from '@thd-olt-component-react/hdhome-catalog-promo';
import { declareContexts, Hydrator } from '@thd-olt-component-react/hydrator';
import { KeyProductFeatures } from '@thd-olt-component-react/key-product-features';
import { LayoutGroup, LayoutManager } from '@thd-olt-component-react/layout-manager';
import { MeasurementGuide } from '@thd-olt-component-react/measurement-guide';
import { MediaGalleryDesktop } from '@thd-olt-component-react/media-gallery';
import { Metadata, ProductMetadata } from '@thd-olt-component-react/metadata';
import { OrderSample } from '@thd-olt-component-react/order-sample';
import { FeatureBasedRecommendationsImporter } from '@thd-olt-component-react/feature-based-recommendations';
import { Price } from '@thd-olt-component-react/price';
import { ProductDetails } from '@thd-olt-component-react/product-details';
import { ProductInfoBar } from '@thd-olt-component-react/product-info-bar';
import { PromotionProducts } from '@thd-olt-component-react/promotion-products';
import { ProductOverviewDesktop, QuickLinks } from '@thd-olt-component-react/product-overview';
import { QuestionsAndAnswersDesktop } from '@thd-olt-component-react/questions-and-answers';
import { CollectionListImporter } from '@thd-olt-component-react/collection-list';
import { RecommendationsPlaceholder, RecommendationsProvider } from '@thd-olt-component-react/recommendations';
import { RelatedSearch } from '@thd-olt-component-react/related-search';
import { ReturnsMessaging } from '@thd-olt-component-react/returns-messaging';
import { useHistory, useLocation, useParams } from '@thd-olt-component-react/router';
import { SalientListItem, SalientPoints } from '@thd-olt-component-react/salient-points';
import { SeoLinks } from '@thd-olt-component-react/seo-links';
import { SpecificationsDesktop } from '@thd-olt-component-react/specifications';
import { Link, Links, StickyNav } from '@thd-olt-component-react/sticky-nav';
import { RatingsAndReviewsDesktopImporter } from '@thd-olt-component-react/ratings-and-reviews';
import { SuperSku } from '@thd-olt-component-react/super-sku';
import { ItemRelatedGroupsDesktopImporter } from '@thd-olt-component-react/item-related-groups';
import { Sharebelt, ShareBeltPrint, ShareBeltShareButton, ShareFavorite } from '@thd-olt-component-react/thd-sharebelt';
import { DynamicRecs } from '@thd-olt-component-react/thd-recs-containers';
import { RecsContext } from '~/@thd-olt-component-react/recommendations/dist/components/RecsContext';
import { SponsoredPipBanner } from '@thd-olt-component-react/sponsored-content';

import {
  shouldDisplayPipInStock,
  shouldDisplayReferrerRecommendations,
  shouldDisplayRequiredItems,
  shouldDisplayVisuallySimilar,
  shouldDisplayCompleteTheLook
} from './product-utils';
import { requestKey } from '../../constants';
import '../../styles/product-page.scss';

const ZoneCard = ({ children }) => {
  return <div className="zone-card">{children}</div>;
};

ZoneCard.propTypes = {
  children: node.isRequired
};

export const Product = () => {
  declareContexts([RecsContext, QueryContext, ExperienceContext]);
  const HYDRATION_STANDARD_SCROLL_BUFFER = 750;
  const ctx = useContext(ExperienceContext);
  const qctx = useContext(QueryContext);
  const { channel, segment = '', isDebugMode } = ctx;
  const arRef = useRef(null);
  const { itemId } = useParams();
  const storeId = useStoreId();
  const history = useHistory();
  const { pathname } = useLocation();
  const marqueeRef = React.createRef();
  const marqueeRowRef = React.createRef();
  const resx = {
    appId: 'homedepot01',
    carouselOptions: {
      fullScrollSkip: 2
    },
    criteo: {
      version: 'model2'
    },
    card: true,
    event: 'product',
    maxProducts: 16,
    products: itemId,
    showATC: true
  };

  const [recommendationsState, setRecommendationsState] = useState({
    ready: false,
    schema: {
      pipInStock: false,
      pipSem: false,
      pipSponsored: true,
      required_items: false,
      visuallySimilar_1_0: false,
      completeTheLook: false
    }
  });

  const handleARClick = () => {
    arRef.current.click();
  };

  const organicSearchSchema = 'pipsem';

  const onChange = (data = {}) => {
    const { canonicalUrl } = data;
    if (canonicalUrl) {
      history.push(canonicalUrl, { disableScroll: true });
    }
  };

  const { data: productData } = useDataModel('clientOnlyProduct', {
    variables: {
      itemId,
      storeId
    },
    skip: !itemId,
    ssr: false
  });

  useEffect(() => {
    if (productData) {
      setRecommendationsState({
        ready: true,
        schema: {
          ...recommendationsState.schema,
          pipInStock: shouldDisplayPipInStock(productData),
          pipSem: shouldDisplayReferrerRecommendations(),
          required_items: shouldDisplayRequiredItems(productData),
          visuallySimilar_1_0: shouldDisplayVisuallySimilar(productData),
          completeTheLook: shouldDisplayCompleteTheLook(productData)
        }
      });
    }
  }, [productData]);

  const seeMoreDetailsSectionClick = () => {
    if (typeof window !== 'undefined') {
      if (document.getElementById('product-overview') === null) {
        window.scrollTo({ top: document.getElementById('product-section-overview').offsetTop, behavior: 'smooth' });
        setTimeout(() => {
          window.scrollTo({ top: document.getElementById('product-section-overview').offsetTop, behavior: 'smooth' });
        }, 2000);
      } else {
        window.scrollTo({ top: document.getElementById('product-overview').offsetTop, behavior: 'smooth' });
      }
    }
  };

  return (
    <LayoutManager
      name="pip-wrapper"
      useContextForProps
    >
      <LayoutGroup name="redirector">
        <PIPRedirector
          disableCanonicalRedirect
          disableCMMMCRedirect
        />
      </LayoutGroup>
      <LayoutGroup name="product-metadata">
        <Metadata>
          <ProductMetadata itemId={itemId} />
        </Metadata>
      </LayoutGroup>
      <LayoutGroup name="pip-content">
        <RecommendationsProvider
          ready={recommendationsState.ready}
          resx={resx}
        >
          <Row className="isBound pip-page">
            <LayoutManager
              data
              input={{
                channel,
                isDebugMode,
                itemId,
                segment
              }}
              name="pip-master"
              useContextForProps
            >
              <LayoutGroup
                name="sticknav"
                tag={Row}
              >
                <StickyNav itemId={itemId}>
                  <Links>
                    <Link href="#product-section-overview">Product Overview</Link>
                    <Link href="#specifications">Specifications</Link>
                    <Link href="#product-section-qa">Questions & Answers</Link>
                    <Link href="#product-section-rr">Customer Reviews</Link>
                  </Links>
                </StickyNav>
              </LayoutGroup>
              <LayoutGroup
                className="u--paddingTop u--paddingBottom"
                name="breadcrumb-sharebelt-grp"
                tag={Row}
              >
                <LayoutManager
                  name="breadcrumb-productInfoBar"
                  useContextForProps
                >
                  <LayoutGroup
                    flatten
                    className="u__visible"
                    name="breadcrumbs"
                    sm="7"
                    tag={Col}
                  >
                    <Breadcrumbs
                      itemId={itemId}
                    />
                  </LayoutGroup>
                  <LayoutGroup
                    flatten
                    name="product-info-bar"
                    sm="5"
                    tag={Col}
                  >
                    <div
                      className="product-info-bar--hide-mobile u__flex-end"
                    >
                      <ProductInfoBar
                        itemId={itemId}
                      />
                    </div>
                  </LayoutGroup>
                </LayoutManager>
              </LayoutGroup>
              {(recommendationsState.schema.pipSem)
              && (
                <LayoutGroup
                  id="recs:organic_search"
                  name="recs:organic_search"
                  tag={Row}
                >
                  <Col nopadding>
                    <RecommendationsPlaceholder
                      hideRecommendation
                      scheme={organicSearchSchema}
                    />
                  </Col>
                </LayoutGroup>
              )}
              <LayoutGroup
                name="zone-a"
                tag={Row}
              >
                <Col nopadding>
                  <Row forwardedRef={marqueeRowRef}>
                    <Col>
                      <Discontinued itemId={itemId} />
                    </Col>
                    <Col
                      className="sticky"
                      flatten
                      sm="7"
                    >
                      <Col
                        className="zone-card overflow"
                        sm="12"
                      >
                        <div ref={marqueeRef}>

                          <LayoutManager
                            name="product-details-share"
                            useContextForProps
                          >
                            <LayoutGroup
                              name="product-details"
                              sm="12"
                              tag={Col}
                            >
                              <ProductDetails itemId={itemId}>
                                <ProductDetails.Badge itemId={itemId} />
                                <ProductDetails.BrandCollection showBrandHyperLink />
                                <div className="product-details__badge-title--wrapper">
                                  <ProductDetails.Title />
                                </div>
                                <div className="product-details__favorites">
                                  <Sharebelt itemId={itemId}>
                                    <ShareFavorite
                                      showCircle
                                      showFavoritesCount
                                    />
                                  </Sharebelt>
                                </div>
                                <ProductDetails.Ratings
                                  offsetselector="#sticky-nav"
                                  qaselector="#product-section-qa"
                                  showBadge
                                  target="#product-section-rr"
                                />
                              </ProductDetails>
                            </LayoutGroup>
                          </LayoutManager>

                          <MediaGalleryDesktop
                            highRes
                            hover
                            itemId={itemId}
                            marqueeRef={marqueeRef}
                            marqueeRowRef={marqueeRowRef}
                            onMediaClick={handleARClick}
                            showLoader
                          />

                          <LayoutGroup
                            name="sharebelt"
                            tag={Col}
                          >
                            <Sharebelt
                              horizontal
                              itemId={itemId}
                            >
                              <ShareBeltShareButton email />
                              <ShareBeltPrint />
                            </Sharebelt>
                          </LayoutGroup>

                        </div>
                      </Col>
                    </Col>
                    <Col
                      flatten
                      sm="5"
                    >
                      <div className="overflow">
                        <Row>
                          <LayoutManager
                            input={{
                              channel,
                              segment
                            }}
                            name="buy-area"
                            useContextForProps
                          >
                            <LayoutGroup
                              name="price"
                              tag={Col}
                              className="zone-card overflow"
                            >
                              <Price
                                channel="desktop"
                                disableRangePricing
                                displayEachUom={false}
                                itemId={itemId}
                                large
                                clsRemediation={{
                                  placeholders: true,
                                  preservePlaceholders: true
                                }}
                              />
                            </LayoutGroup>
                            <LayoutGroup
                              name="consumer-card-messaging"
                              tag={Col}
                              className="zone-card overflow consumer-credit-messaging__card"
                            >
                              <ConsumerCardMessaging
                                itemId={itemId}
                                clsRemediation={{
                                  placeholders: true,
                                  preservePlaceholders: true
                                }}
                                isFinancePromoEligible
                              />
                            </LayoutGroup>
                            <LayoutGroup
                              className="zone-card overflow"
                              name="promotion-products"
                              tag={Col}
                            >
                              <PromotionProducts
                                itemId={itemId}
                              />
                            </LayoutGroup>
                            <LayoutGroup
                              className="zone-card"
                              name="salient"
                              tag={Col}
                            >
                              <SalientPoints
                                itemId={itemId}
                                max={3}
                              >
                                <SalientListItem>
                                  {/* eslint-disable-next-line */}
                                  <a className="see-details u__default-link" href="#product-overview" onClick={seeMoreDetailsSectionClick}>See More Details</a>
                                </SalientListItem>
                              </SalientPoints>
                            </LayoutGroup>
                            <LayoutGroup
                              name="measurement-guide"
                              tag={Row}
                            >
                              <div className="zone-card full-bleed">
                                <MeasurementGuide />
                              </div>
                            </LayoutGroup>
                            <LayoutGroup
                              name="aisle-bay"
                              className="zone-card overflow"
                              tag={Col}
                            >
                              <AisleBay
                                itemId={itemId}
                              />
                            </LayoutGroup>
                            <LayoutGroup
                              className="zone-card overflow"
                              name="super-sku"
                              tag={Col}
                            >
                              <SuperSku
                                itemId={itemId}
                                onChange={onChange}
                                shouldSyncAttributes
                              />
                            </LayoutGroup>
                            <LayoutGroup
                              className="zone-card"
                              name="order-sample overflow"
                              tag={Col}
                            >
                              <OrderSample itemId={itemId}>
                                Need a closer look?
                              </OrderSample>
                            </LayoutGroup>
                            <LayoutGroup
                              flatten
                              name="buybox"
                              tag={Col}
                            >
                              <Buybox
                                itemId={itemId}
                                lmUseContextForProps
                                sticky
                                showCards
                              />
                            </LayoutGroup>
                            <LayoutManager
                              input={{
                                channel,
                                segment
                              }}
                              name="returns-area"
                              useContextForProps
                            >
                              <LayoutGroup
                                className="zone-card overflow"
                                name="returns-messaging"
                              >
                                <ReturnsMessaging itemId={itemId} />
                              </LayoutGroup>
                            </LayoutManager>
                            <LayoutGroup
                              className="zone-card overflow"
                              name="gcc"
                              tag={Col}
                            >
                              <GlobalCustomConfigurator itemId={itemId} />
                            </LayoutGroup>
                          </LayoutManager>
                        </Row>
                      </div>
                    </Col>
                  </Row>
                </Col>
              </LayoutGroup>
              <LayoutGroup
                name="augmented-reality"
                tag={Row}
              >
                <div className="zone-card">
                  <AugmentedReality
                    forwardedRef={arRef}
                    instructions
                    itemId={itemId}
                  />
                </div>
              </LayoutGroup>
              {recommendationsState.schema.pipInStock && (
                <RecommendationsPlaceholder
                  scheme="pipinstock"
                  layoutGroup={{
                    name: 'pipinstock',
                    tag: Row,
                    tagProps: {
                      className: 'child-no-pad'
                    }
                  }}
                />
              )}
              {(recommendationsState.schema.required_items) && (
                <RecommendationsPlaceholder
                  defer
                  scheme="required_items"
                  layoutGroup={{
                    tag: Row,
                    name: 'required_items',
                    tagProps: {
                      className: 'child-no-pad'
                    }
                  }}
                />
              )}

              <FrequentlyBoughtTogether
                itemId={itemId}
                openInNewTab
                layoutGroup={{
                  tag: Row,
                }}
                dynamic={{
                  pageType: 'pip'
                }}
                hydrator={{
                  className: 'grid',
                  tag: ZoneCard
                }}
              />

              <SponsoredPipBanner
                pageContext={{
                  label: 'pip',
                  data: {
                    itemId: itemId,
                    treatment: 'A'
                  },
                }}
                layoutGroup={{
                  name:"pip-sponsored-banner-A"
                }}
                dynamic={{
                  pageType: 'pip'
                }}
                hydrator={{
                  id: 'hydrated-sponsored-pip-banner-A'
                }}
              />

              <SpecificationsDesktop
                itemId={itemId}
                layoutGroup={{
                  tag: Row
                }}
                dynamic={{
                  pageType: 'pip'
                }}
                hydrator={{
                  tag: ZoneCard,
                  className: 'grid'
                }}
              >
                <KeyProductFeatures
                  itemId={itemId}
                />
              </SpecificationsDesktop>
              <ProductOverviewDesktop
                channel={channel}
                itemId={itemId}
                showReturnMessage
                revJet
                hydrator={{
                  className: 'grid',
                  tag: ZoneCard
                }}
                layoutGroup={{
                  tag: Row,
                }}
                dynamic={{
                  pageType: 'pip'
                }}
              />
              <SponsoredPipBanner
                pageContext={{
                  label: 'pip',
                  data: {
                    itemId: itemId,
                    treatment: 'B'
                  },
                }}
                layoutGroup={{
                  name:"pip-sponsored-banner-B"
                }}
                dynamic={{
                  pageType: 'pip'
                }}
                hydrator={{
                  id: 'hydrated-sponsored-pip-banner-B',
                  scrollBuffer: HYDRATION_STANDARD_SCROLL_BUFFER
                }}
              />
              <EducationalSummary
                itemId={itemId}
                layoutGroup={{
                  tag: Row,
                }}
                dynamic={{
                  pageType: 'pip'
                }}
                hydrator={{
                  className: 'grid'
                }}
              />
              <CollectionListImporter
                itemId={itemId}
                qctx={qctx}
                layoutGroup={{
                  tag: Row,
                }}
                dynamic={{
                  pageType: 'pip'
                }}
                hydrator={{
                  className: 'overflow grid',
                  tag: ZoneCard
                }}
              />

              {(recommendationsState.ready && !recommendationsState.schema.pipInStock) && (
                <RecommendationsPlaceholder
                  defer
                  scheme="pip_alternatives"
                  layoutGroup={{
                    name: 'alternatives',
                    tag: Row,
                    tagProps: {
                      className: 'child-no-pad'
                    }
                  }}
                />
              )}
              <RecommendationsPlaceholder
                defer
                scheme="pip_sponsored"
                layoutGroup={{
                  name: 'pip_sponsored',
                  tag: Row,
                  tagProps: {
                    className: 'child-no-pad'
                  }
                }}
              />

              <FeatureBasedRecommendationsImporter
                hideOnMobile
                itemId={itemId}
                qctx={qctx}
                layoutGroup={{
                  tag: Row,
                }}
                hydrator={{
                  tag: ZoneCard,
                  className: 'grid'
                }}
                dynamic={{
                  pageType: 'pip'
                }}
              />

              {(recommendationsState.schema.visuallySimilar_1_0) && (
                <RecommendationsPlaceholder
                  defer
                  itemId={itemId}
                  scheme="visuallysimilar_1_0"
                  layoutGroup={{
                    tag: Row,
                    name: 'visually-similar',
                    tagProps: {
                      className: 'child-no-pad'
                    }
                  }}
                />
              )}
              {(recommendationsState.schema.completeTheLook) && (
                <LayoutGroup
                  name="complete-the-look"
                  tag={Row}
                >
                  <Col nopadding className="zone-card">
                    <DynamicRecs
                      schemaName="ctl"
                      anchor={itemId}
                      collectionUrl={productData?.details?.collection?.url}
                      requestKey={requestKey}
                      showLoading
                      hideFulfillment
                    />
                  </Col>
                </LayoutGroup>
              )}
              <ItemRelatedGroupsDesktopImporter
                accordion={false}
                dsNext
                itemId={itemId}
                maxItems={7}
                qctx={qctx}
                rowHeader="Get Everything You Need"
                layoutGroup={{
                  tag: Row,
                }}
                dynamic={{
                  pageType: 'pip'
                }}
                hydrator={{
                  className: 'zone-card col__12-12',
                }}
              />
              <HdhomeCatalogPromo
                formSubmission
                layoutGroup={{
                  tag: Row,
                }}
                dynamic={{
                  pageType: 'pip',
                }}
                hydrator={{
                  className: 'grid',
                  tag: ZoneCard
                }}
              />
              <QuestionsAndAnswersDesktop
                itemId={itemId}
                layoutGroup={{
                  tag: Row,
                }}
                dynamic={{
                  pageType: 'pip',
                }}
                hydrator={{
                  className: 'grid',
                  tag: ZoneCard
                }}
              />
              <RatingsAndReviewsDesktopImporter
                itemId={itemId}
                prosAndCons
                qctx={qctx}
                layoutGroup={{
                  tag: Row,
                }}
                dynamic={{
                  pageType: 'pip'
                }}
                hydrator={{
                  className: 'grid',
                  tag: ZoneCard
                }}
              />
              <RecommendationsPlaceholder
                defer
                scheme="rv_gm_pip_rr"
                layoutGroup={{
                  name: 'recently-viewed',
                  tag: Row,
                  tagProps: {
                    className: 'child-no-pad',
                  }
                }}
              />
              <RelatedSearch
                itemId={itemId}
                layoutGroup={{
                  tag: Row,
                }}
                dynamic={{
                  pageType: 'pip'
                }}
                hydrator={{
                  className: 'col__12-12'
                }}
              />
              <SeoLinks
                path={pathname}
                layoutGroup={{
                  tag: Row
                }}
                dynamic={{
                  pageType: 'pip',
                }}
                hydrator={{
                  className: 'grid'
                }}
              />
            </LayoutManager>
          </Row>
        </RecommendationsProvider>
      </LayoutGroup>
    </LayoutManager>
  );
};

Product.displayName = 'ProductPage';

Product.propTypes = {};

Product.dataModel = extend(
  {
    clientOnlyProduct: params({ itemId: string().isRequired })
      .shape({
        itemId: string(),
        dataSources: string(),
        info: shape({
          recommendationFlags: shape({
            reqItems: bool(),
            visualNavigation: bool()
          }),
          returnable: string()
        }),
        availabilityType: shape({
          discontinued: bool(),
          type: string()
        }),
        fulfillment: params({ storeId: string() })
          .shape({
            backordered: bool(),
            fulfillmentOptions: arrayOf(shape({
              type: string(),
              fulfillable: bool(),
              services: arrayOf(shape({
                type: string(),
                locations: arrayOf(shape({
                  isAnchor: bool(),
                  inventory: shape({
                    isLimitedQuantity: bool(),
                    isOutOfStock: bool()
                  })
                }))
              }))
            }))
          })
      })
  },
  {
    clientOnlyProduct: params({ itemId: string().isRequired })
      .shape({
        seoDescription: string()
      })
  },
  PIPRedirector,
  ProductMetadata,
  StickyNav,
  Breadcrumbs,
  Sharebelt,
  RecommendationsPlaceholder,
  Discontinued,
  ProductInfoBar,
  MediaGalleryDesktop,
  AisleBay,
  ProductDetails,
  SalientPoints,
  Price,
  ConsumerCardMessaging,
  OrderSample,
  SuperSku,
  Buybox,
  AugmentedReality,
  FrequentlyBoughtTogether,
  ProductOverviewDesktop,
  PromotionProducts,
  FeatureBasedRecommendationsImporter,
  ItemRelatedGroupsDesktopImporter,
  SpecificationsDesktop,
  QuestionsAndAnswersDesktop,
  RatingsAndReviewsDesktopImporter,
  SeoLinks,
  RelatedSearch,
  CollectionListImporter,
  GlobalCustomConfigurator
);
